import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider, appleProvider } from '../firebase/config';
import { isExemptFromOnboarding, isOnboardingRequired } from '../utils/onboardingHelper';
import { getPostLoginRoute } from '../utils/routing';
import { 
  User, 
  Building, 
  ArrowRight, 
  ArrowLeft, 
  Search, 
  Heart, 
  MessageCircle, 
  Calendar,
  Shield,
  Star,
  CheckCircle,
  Building2,
  Users,
  FileText,
  Clock,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Briefcase,
  Rocket,
  Home
} from 'lucide-react';
import StudyStayLogo from '../components/StudyStayLogo';
import toast from 'react-hot-toast';

export default function UserTypeSelectionLogin() {
  const location = useLocation();

  // Pre-compute initial state from URL params to avoid flash of wrong step
  const initialParams = new URLSearchParams(location.search || '');
  const initialSource = initialParams.get('source');
  const isInviteOrEarlyAccess = initialSource === 'agency-invite' || initialSource === 'coming-soon';
  const requestedUserType =
    initialParams.get('userType') === 'landlord'
      ? 'landlord'
      : (initialParams.get('userType') === 'tenant' ? 'tenant' : null);
  const requestedLandlordType =
    initialParams.get('landlordType') === 'agency'
      ? 'agency'
      : (initialParams.get('landlordType') === 'private' ? 'private' : null);
  const shouldSkipUserTypeStep = initialSource === 'welcome' && !!requestedUserType;

  const [step, setStep] = useState(() => {
    const mode = initialParams.get('mode');
    if (!isInviteOrEarlyAccess) {
      if (requestedUserType === 'tenant') return 'login';
      if (requestedUserType === 'landlord') {
        if (requestedLandlordType) return mode === 'register' ? 'register' : 'login';
        return 'landlordType';
      }
      return 'userType';
    }
    return mode === 'login' ? 'login' : 'register';
  });
  const [selectedUserType, setSelectedUserType] = useState(() =>
    isInviteOrEarlyAccess ? 'landlord' : requestedUserType
  );
  const [selectedLandlordType, setSelectedLandlordType] = useState(() => {
    if (!isInviteOrEarlyAccess) return requestedLandlordType;
    const lt = initialParams.get('landlordType');
    return lt === 'agency' ? 'agency' : 'private';
  });
  const [pendingPlan, setPendingPlan] = useState(() =>
    isInviteOrEarlyAccess ? (initialParams.get('plan') || 'starter') : 'starter'
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(() =>
    isInviteOrEarlyAccess ? (initialParams.get('email') || '') : ''
  );
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [openPolicy, setOpenPolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const redirectAfterLogin = initialParams.get('redirect');

  // Store inviteId in sessionStorage on mount if from agency-invite
  useEffect(() => {
    if (initialSource === 'agency-invite') {
      const inviteId = initialParams.get('inviteId');
      if (inviteId) sessionStorage.setItem('pendingAgencyInviteId', inviteId);
    }
  }, []);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Handle URL param changes after mount (e.g. in-page navigation)
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const source = params.get('source');
    const userType = params.get('userType');
    const landlordType = params.get('landlordType');
    if (source !== 'coming-soon' && source !== 'agency-invite') return;

    const requestedPlan = params.get('plan') || sessionStorage.getItem('earlyAccessPlan') || 'starter';
    const requestedLandlordType =
      params.get('landlordType') || sessionStorage.getItem('earlyAccessLandlordType') || 'private';
    const requestedMode = params.get('mode');
    const requestedEmail = params.get('email');
    const inviteId = params.get('inviteId');

    setPendingPlan(requestedPlan);
    setSelectedUserType('landlord');
    setSelectedLandlordType(requestedLandlordType === 'agency' ? 'agency' : 'private');
    setStep(requestedMode === 'login' ? 'login' : 'register');
    if (requestedEmail) setEmail(requestedEmail);
    if (source === 'agency-invite' && inviteId) {
      sessionStorage.setItem('pendingAgencyInviteId', inviteId);
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const source = params.get('source');
    if (source === 'coming-soon' || source === 'agency-invite') return;

    const userType = params.get('userType');
    const landlordType = params.get('landlordType');
    if (userType === 'tenant') {
      setSelectedUserType('tenant');
      setSelectedLandlordType(null);
      setStep('login');
      return;
    }
    if (userType === 'landlord') {
      setSelectedUserType('landlord');
      const mode = params.get('mode');
      if (landlordType === 'agency' || landlordType === 'private') {
        setSelectedLandlordType(landlordType);
        setStep(mode === 'register' ? 'register' : 'login');
      } else {
        setStep('landlordType');
      }
    }
  }, [location.search]);

  // Safety net: when coming from welcome with userType in URL,
  // never show the generic user type chooser again.
  useEffect(() => {
    if (!shouldSkipUserTypeStep) return;
    if (requestedUserType === 'tenant') {
      setSelectedUserType('tenant');
      setSelectedLandlordType(null);
      if (step === 'userType') setStep('login');
      return;
    }
    if (requestedUserType === 'landlord') {
      setSelectedUserType('landlord');
      if (requestedLandlordType === 'agency' || requestedLandlordType === 'private') {
        setSelectedLandlordType(requestedLandlordType);
        const requestedMode = initialParams.get('mode');
        if (step === 'userType' || step === 'landlordType') {
          setStep(requestedMode === 'register' ? 'register' : 'login');
        }
      } else {
        if (step === 'userType') setStep('landlordType');
      }
    }
  }, [shouldSkipUserTypeStep, requestedUserType, requestedLandlordType, step]);

  const handleUserTypeSelection = (userType) => {
    setSelectedUserType(userType);
    // If landlord is selected, show landlord type selection first
    if (userType === 'landlord') {
      setStep('landlordType');
    } else {
      setStep('login');
    }
  };

  const handleLandlordTypeSelection = (landlordType) => {
    setSelectedLandlordType(landlordType);
    setStep('login'); // Then proceed to login/register form
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for register step
    if (step === 'register') {
      if (!firstName || !lastName || !email || !password) {
        toast.error('Please fill in all fields');
        return;
      }
      
      if (firstName.trim().length < 2) {
        toast.error('First name must be at least 2 characters');
        return;
      }
      
      if (lastName.trim().length < 2) {
        toast.error('Last name must be at least 2 characters');
        return;
      }

      // If landlord, check if landlordType is selected
      if (selectedUserType === 'landlord' && !selectedLandlordType) {
        toast.error('Selecteer eerst het type verhuurder (Particulier of Agency)');
        return;
      }

      if (!acceptedTerms || !acceptedPrivacy) {
        toast.error('Je moet akkoord gaan met de Terms of Service en Privacy Policy');
        return;
      }
    } else {
      // Validation for login step
      if (!email || !password) {
        toast.error('Please fill in all fields');
        return;
      }
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      console.log(`${step} attempt for:`, email, 'UserType:', selectedUserType);
      
      let user;
      if (step === 'register') {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        user = result.user;
        console.log('Registration successful:', user.uid);
        
        // Send email verification
        try {
          const { sendEmailVerification } = await import('firebase/auth');
          await sendEmailVerification(user, {
            url: 'https://studystay.be/auth/action',
            handleCodeInApp: false
          });
          console.log('✅ Verification email sent to:', email);
        } catch (verificationError) {
          console.error('❌ Error sending verification email:', verificationError);
          toast.error('Account created, but verification email could not be sent. Please try resending from the verification page.');
        }
        
        const fullName = `${firstName.trim()} ${lastName.trim()}`;
        
        const userData = {
          email: user.email,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          displayName: fullName,
          userType: selectedUserType,
          accountType: selectedUserType,
          type: selectedUserType,
          createdAt: new Date(),
          updatedAt: new Date(),
          name: fullName,
          contactName: fullName,
          phone: '',
          address: '',
          bio: '',
          profileImageUrl: '',
          isActive: true,
          onboardingCompleted: selectedUserType === 'landlord' ? isExemptFromOnboarding(email) : true, // Landlords need onboarding unless exempt
          acceptedTermsOfService: true,
          acceptedPrivacyPolicy: true,
          legalConsents: {
            termsAcceptedAt: new Date(),
            privacyAcceptedAt: new Date(),
            source: 'web_register'
          }
        };

        // If landlord, add landlordType
        if (selectedUserType === 'landlord' && selectedLandlordType) {
          userData.landlordType = selectedLandlordType;
        }
        if (selectedUserType === 'landlord' && pendingPlan && pendingPlan !== 'starter') {
          userData.earlyAccessPlan = pendingPlan;
          userData.selectedPlan = pendingPlan;
        }

        await setDoc(doc(db, 'users', user.uid), userData);
        
        toast.success('Account created! Please verify your email address.');
        
        // If landlord, redirect to onboarding after email verification
        if (selectedUserType === 'landlord') {
          // Store that they need onboarding after verification
          sessionStorage.setItem('needsOnboarding', 'true');
        }
        
        // Redirect to verification page
        navigate('/email-verification');
        return;
      } else {
        const result = await signInWithEmailAndPassword(auth, email, password);
        user = result.user;
        console.log('Login successful:', user.uid);
        let resolvedUserType = selectedUserType || 'tenant';
        let resolvedLandlordType = selectedLandlordType || null;
        
        // Check if email is verified
        const { reload } = await import('firebase/auth');
        await reload(user);
        
        if (!user.emailVerified) {
          toast.error('Please verify your email address before logging in.');
          // Don't sign out - redirect to verification page
          // User stays logged in so they can verify their email
          navigate('/email-verification');
          return;
        }
        
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('Existing user data:', userData);
          
          const existingUserType = userData.userType || userData.accountType || userData.type;
          const finalUserType = selectedUserType || existingUserType || 'tenant';
          resolvedUserType = finalUserType;
          resolvedLandlordType = selectedLandlordType || userData.landlordType || null;
          
          console.log('Final user type:', finalUserType);
          
          if (selectedUserType && selectedUserType !== existingUserType) {
            console.log('Updating user type from', existingUserType, 'to', selectedUserType);
            await setDoc(doc(db, 'users', user.uid), {
              userType: selectedUserType,
              accountType: selectedUserType,
              type: selectedUserType,
              updatedAt: new Date()
            }, { merge: true });
          }

          if (finalUserType === 'landlord' && selectedLandlordType) {
            await setDoc(doc(db, 'users', user.uid), {
              landlordType: selectedLandlordType,
              updatedAt: new Date()
            }, { merge: true });
          }

          if (finalUserType === 'landlord' && pendingPlan && pendingPlan !== 'starter') {
            await setDoc(doc(db, 'users', user.uid), {
              earlyAccessPlan: pendingPlan,
              selectedPlan: pendingPlan,
              updatedAt: new Date()
            }, { merge: true });
          }
        } else {
          console.log('No user document found, creating one...');
          const newUserData = {
            email: user.email,
            displayName: user.displayName || '',
            userType: selectedUserType || 'tenant',
            accountType: selectedUserType || 'tenant',
            type: selectedUserType || 'tenant',
            createdAt: new Date(),
            updatedAt: new Date(),
            name: user.displayName || '',
            contactName: user.displayName || '',
            phone: '',
            address: '',
            bio: '',
            profileImageUrl: '',
            isActive: true
          };

          if ((selectedUserType || 'tenant') === 'landlord' && selectedLandlordType) {
            newUserData.landlordType = selectedLandlordType;
          }
          if ((selectedUserType || 'tenant') === 'landlord' && pendingPlan && pendingPlan !== 'starter') {
            newUserData.earlyAccessPlan = pendingPlan;
            newUserData.selectedPlan = pendingPlan;
          }

          await setDoc(doc(db, 'users', user.uid), newUserData);
        }
        
        toast.success('Login successful!');

        if (resolvedUserType === 'landlord' && pendingPlan && pendingPlan !== 'starter') {
          const billingPath = resolvedLandlordType === 'agency'
            ? '/landlord/agency/billing'
            : '/landlord/billing';
          navigate(`${billingPath}?required=1&plan=${pendingPlan}&source=early-access`, { replace: true });
          return;
        }
        
        if (redirectAfterLogin) {
          navigate(redirectAfterLogin, { replace: true });
          return;
        }

        // Use central routing function for correct redirect
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const route = getPostLoginRoute(userData, user.email);
            console.log('UserTypeSelectionLogin: Using central routing, redirecting to:', route);
            navigate(route, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error('Error fetching userType for redirect:', error);
          toast.error('Kon accounttype niet ophalen, je wordt doorgestuurd naar de startpagina.');
          navigate('/', { replace: true });
        }
      }
      
    } catch (error) {
      console.error(`${step} error:`, error);
      
      let errorMessage = `${step} failed. Please try again.`;
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider, providerName) => {
    if (!selectedUserType) {
      toast.error('Selecteer eerst een accounttype.');
      setStep('userType');
      return;
    }
    try {
      setLoading(true);

      // Pre-cache in localStorage so AuthContext picks up the correct values
      // immediately when onAuthStateChanged fires after signInWithPopup.
      try {
        localStorage.setItem('userType', selectedUserType);
        if (selectedUserType === 'landlord' && selectedLandlordType) {
          localStorage.setItem('landlordType', selectedLandlordType);
        }
      } catch { /* best-effort */ }

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const existingUserType = userData.userType || userData.accountType || userData.type;
        const resolvedUserType = selectedUserType || existingUserType || 'tenant';

        if (selectedUserType && selectedUserType !== existingUserType) {
          await setDoc(doc(db, 'users', user.uid), {
            userType: selectedUserType, accountType: selectedUserType, type: selectedUserType, updatedAt: new Date()
          }, { merge: true });
        }
        if (resolvedUserType === 'landlord' && selectedLandlordType) {
          await setDoc(doc(db, 'users', user.uid), { landlordType: selectedLandlordType, updatedAt: new Date() }, { merge: true });
        }

        toast.success(`${providerName} login gelukt!`);
        const freshDoc = await getDoc(doc(db, 'users', user.uid));
        if (freshDoc.exists()) {
          const route = getPostLoginRoute(freshDoc.data(), user.email);
          navigate(route, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else {
        const fullName = user.displayName || '';
        const nameParts = fullName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        const userData = {
          email: user.email,
          displayName: fullName,
          name: fullName,
          contactName: fullName,
          firstName,
          lastName,
          userType: selectedUserType,
          accountType: selectedUserType,
          type: selectedUserType,
          createdAt: new Date(),
          updatedAt: new Date(),
          phone: '',
          address: '',
          bio: '',
          profileImageUrl: '',
          isActive: true,
          onboardingCompleted: selectedUserType === 'landlord' ? isExemptFromOnboarding(user.email) : true,
          authProvider: providerName.toLowerCase()
        };
        if (selectedUserType === 'landlord' && selectedLandlordType) {
          userData.landlordType = selectedLandlordType;
        }
        await setDoc(doc(db, 'users', user.uid), userData);

        toast.success('Account aangemaakt!');
        const route = getPostLoginRoute(userData, user.email);
        navigate(route, { replace: true });
      }
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        return;
      }
      console.error(`${providerName} sign-in error:`, error);
      if (error.code === 'auth/account-exists-with-different-credential') {
        toast.error('Er bestaat al een account met dit e-mailadres via een andere inlogmethode.');
      } else {
        toast.error(`${providerName} inloggen mislukt. Probeer het opnieuw.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (shouldSkipUserTypeStep) {
      navigate('/', { replace: true });
      return;
    }
    if (step === 'login' || step === 'register') {
      if (selectedUserType === 'landlord') {
        setStep('landlordType');
      } else {
        setStep('userType');
      }
    } else if (step === 'landlordType') {
      setStep('userType');
    }
  };

  const switchMode = () => {
    setStep(step === 'login' ? 'register' : 'login');
    // Clear name fields when switching to login
    if (step === 'register') {
      setFirstName('');
      setLastName('');
    }
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 relative overflow-hidden"
    >
      {/* Static StudyStay background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-44 -right-44 w-[32rem] h-[32rem] rounded-full bg-blue-100/40 blur-3xl"></div>
        <div className="absolute -bottom-44 -left-44 w-[30rem] h-[30rem] rounded-full bg-sky-100/40 blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-[20rem] h-[20rem] rounded-full bg-indigo-100/30 blur-3xl"></div>
      </div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl w-full">
          {/* Header */}
          <div className={`text-center mb-12 transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex justify-center mb-6">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-md overflow-hidden transition-all duration-500 hover:scale-110 hover:rotate-3 hover:shadow-xl"
                style={{ backgroundColor: '#60ADF0' }}
              >
                <StudyStayLogo className="w-10 h-10 transition-transform duration-500" />
              </div>
            </div>
            
            {step === 'userType' && (
              <>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
                  Welcome to StudyStay
                </h1>
                <p className="text-lg text-gray-600">
                  Choose your account type to get started
                </p>
              </>
            )}
            
            {step === 'landlordType' && (
              <>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
                  Wat voor verhuurder ben je?
                </h1>
                <p className="text-lg text-gray-600">
                  Kies het type account dat het beste bij je past
                </p>
              </>
            )}

            {(step === 'login' || step === 'register') && (
              <>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
                  {step === 'login' ? 'Sign In' : 'Create Account'}
                </h1>
                <p className="text-lg text-gray-600">
                  {step === 'login' 
                    ? `Sign in as a ${selectedUserType === 'landlord' ? (selectedLandlordType === 'agency' ? 'agency' : 'private landlord') : 'student'}` 
                    : `Create your ${selectedUserType === 'landlord' ? (selectedLandlordType === 'agency' ? 'agency' : 'private landlord') : 'student'} account`
                  }
                </p>
              </>
            )}
          </div>

          {/* User Type Selection */}
          {step === 'userType' && (
            <div className={`grid md:grid-cols-2 gap-6 mb-8 transition-opacity duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              {/* Student Card */}
              <div
                className="cursor-pointer group"
                onClick={() => handleUserTypeSelection('tenant')}
              >
                <div className="bg-white rounded-xl p-8 shadow-sm border-2 border-gray-200 hover:border-ios-blue hover:shadow-md transition-all duration-200 h-full">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">I'm a Student</h3>
                    <p className="text-gray-600">Looking for accommodation</p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Search className="w-5 h-5 text-blue-500" />
                      <span className="text-sm">Browse and search properties</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Heart className="w-5 h-5 text-red-500" />
                      <span className="text-sm">Save favorites</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <MessageCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm">Contact landlords directly</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Calendar className="w-5 h-5 text-purple-500" />
                      <span className="text-sm">Schedule viewings</span>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-center space-x-4 text-xs text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Shield className="w-4 h-4" />
                        <span>100% Free</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4" />
                        <span>Verified</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Landlord Card */}
              <div
                className="cursor-pointer group"
                onClick={() => handleUserTypeSelection('landlord')}
              >
                <div className="bg-white rounded-xl p-8 shadow-sm border-2 border-gray-200 hover:border-ios-blue hover:shadow-md transition-all duration-200 h-full">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-600 transition-colors">
                      <Building className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">I'm a Landlord</h3>
                    <p className="text-gray-600">Renting out properties</p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Building2 className="w-5 h-5 text-orange-500" />
                      <span className="text-sm">List and manage properties</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Users className="w-5 h-5 text-blue-500" />
                      <span className="text-sm">Connect with students</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <FileText className="w-5 h-5 text-green-500" />
                      <span className="text-sm">Manage contracts</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Clock className="w-5 h-5 text-purple-500" />
                      <span className="text-sm">Schedule viewings</span>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-center space-x-4 text-xs text-gray-600">
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="w-4 h-4" />
                        <span>CRM Tools</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Shield className="w-4 h-4" />
                        <span>Secure</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Landlord Type Selection */}
          {step === 'landlordType' && (
            <div className={`grid md:grid-cols-2 gap-6 mb-8 transition-opacity duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              {/* Private Landlord Card */}
              <div
                className={`cursor-pointer group transform transition-all duration-300 ${
                  selectedLandlordType === 'private' ? 'scale-105' : 'hover:scale-102'
                }`}
                onClick={() => handleLandlordTypeSelection('private')}
              >
                <div className={`bg-white rounded-xl p-8 shadow-sm border-2 transition-all duration-200 h-full ${
                  selectedLandlordType === 'private' 
                    ? 'border-ios-blue shadow-md' 
                    : 'border-gray-200 hover:border-ios-blue hover:shadow-md'
                }`}>
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 transition-colors ${
                      selectedLandlordType === 'private' ? 'bg-blue-600' : 'bg-blue-500 group-hover:bg-blue-600'
                    }`}>
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Particulier</h3>
                    <p className="text-gray-600">Ik verhuur zelf mijn pand(en)</p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Home className="w-5 h-5 text-blue-500" />
                      <span className="text-sm">Beheer je eigen panden</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Users className="w-5 h-5 text-green-500" />
                      <span className="text-sm">Direct contact met studenten</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <FileText className="w-5 h-5 text-purple-500" />
                      <span className="text-sm">Volledige controle over contracten</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Calendar className="w-5 h-5 text-orange-500" />
                      <span className="text-sm">Plan je eigen bezichtigingen</span>
                    </div>
                  </div>

                  {selectedLandlordType === 'private' && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-center space-x-2 text-sm text-ios-blue font-semibold">
                        <CheckCircle className="w-5 h-5" />
                        <span>Geselecteerd</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Agency Card — Coming Soon */}
              <div
                className="relative group transform transition-all duration-300 opacity-60 cursor-not-allowed"
                onClick={() => toast('Agency accounts komen binnenkort beschikbaar!', { icon: '🚀' })}
              >
                <div className="absolute top-4 right-4 z-10 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  Coming Soon
                </div>
                <div className="bg-white rounded-xl p-8 shadow-sm border-2 border-gray-200 h-full">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 bg-orange-400">
                      <Building2 className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-400 mb-2">Agency / Makelaar</h3>
                    <p className="text-gray-400">Ik beheer panden voor anderen</p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Building2 className="w-5 h-5 text-orange-500" />
                      <span className="text-sm">Beheer meerdere panden</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Briefcase className="w-5 h-5 text-blue-500" />
                      <span className="text-sm">Professioneel account</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Users className="w-5 h-5 text-green-500" />
                      <span className="text-sm">Help particuliere verhuurders</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-700">
                      <Rocket className="w-5 h-5 text-purple-500" />
                      <span className="text-sm">Smart filtering & suggesties</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}

          {/* Login/Register Form */}
          {(step === 'login' || step === 'register') && (
            <div className={`max-w-md mx-auto transition-opacity duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                {/* Back Button */}
                <button
                  onClick={goBack}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm font-medium">Back</span>
                </button>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name Fields - Only show for register */}
                  {step === 'register' && (
                    <>
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                          First Name
                        </label>
                        <input
                          id="firstName"
                          name="firstName"
                          type="text"
                          autoComplete="given-name"
                          required={step === 'register'}
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ios-blue focus:border-transparent transition-all"
                          placeholder="Enter your first name"
                        />
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                          Last Name
                        </label>
                        <input
                          id="lastName"
                          name="lastName"
                          type="text"
                          autoComplete="family-name"
                          required={step === 'register'}
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="block w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ios-blue focus:border-transparent transition-all"
                          placeholder="Enter your last name"
                        />
                      </div>
                    </>
                  )}

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ios-blue focus:border-transparent transition-all"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ios-blue focus:border-transparent transition-all"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Legal consents - required for account creation */}
                  {step === 'register' && (
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <label className="flex items-start space-x-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-ios-blue focus:ring-ios-blue"
                        />
                        <span>
                          Ik ga akkoord met de{' '}
                          <button
                            type="button"
                            onClick={() => setOpenPolicy({ title: 'Terms of Service', path: '/legal/terms-of-service' })}
                            className="font-medium text-ios-blue hover:underline"
                          >
                            Terms of Service
                          </button>
                          .
                        </span>
                      </label>

                      <label className="flex items-start space-x-3 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={acceptedPrivacy}
                          onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-ios-blue focus:ring-ios-blue"
                        />
                        <span>
                          Ik heb de{' '}
                          <button
                            type="button"
                            onClick={() => setOpenPolicy({ title: 'Privacy Policy', path: '/legal/privacy-policy' })}
                            className="font-medium text-ios-blue hover:underline"
                          >
                            Privacy Policy
                          </button>
                          {' '}gelezen en ga akkoord met de verwerking van mijn persoonsgegevens zoals daarin beschreven.
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="group w-full flex justify-center items-center space-x-2 py-3 px-6 bg-ios-blue text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ios-blue disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Please wait...</span>
                        </>
                      ) : (
                        <>
                          <span className="transition-transform duration-300 group-hover:translate-x-1">{step === 'login' ? 'Sign In' : 'Create Account'}</span>
                          <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* OAuth divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white text-gray-500">of ga verder met</span>
                  </div>
                </div>

                {/* OAuth Buttons */}
                <div className="space-y-3">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleOAuthSignIn(googleProvider, 'Google')}
                    className="w-full flex items-center justify-center space-x-3 py-3 px-4 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 transition-all duration-200 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <span className="text-sm font-medium text-gray-700">Doorgaan met Google</span>
                  </button>

                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleOAuthSignIn(appleProvider, 'Apple')}
                    className="w-full flex items-center justify-center space-x-3 py-3 px-4 border border-gray-300 rounded-lg bg-black hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    <span className="text-sm font-medium text-white">Doorgaan met Apple</span>
                  </button>
                </div>

                {/* Toggle Mode */}
                <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                  <button
                    onClick={switchMode}
                    className="text-sm text-gray-600 hover:text-ios-blue font-medium transition-colors"
                  >
                    {step === 'login' 
                      ? "Don't have an account? Create one" 
                      : "Already have an account? Sign in"
                    }
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Back to Welcome */}
          <div className={`text-center mt-8 transition-opacity duration-700 delay-600 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <button
              onClick={() => navigate('/welcome')}
              className="text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              ← Back to welcome
            </button>
          </div>
        </div>
      </div>

      {openPolicy && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-5xl h-[85vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{openPolicy.title}</h3>
              <button
                type="button"
                onClick={() => setOpenPolicy(null)}
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Sluiten
              </button>
            </div>
            <iframe
              title={openPolicy.title}
              src={openPolicy.path}
              className="w-full flex-1 border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
}
